import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  X, Save, Code, FileText, Variable,
  ChevronDown, ChevronRight, Copy, CheckCircle
} from 'lucide-react';

import { API_BASE_URL, getAuthHeaders as getCoreAuthHeaders } from '../../api/core.js';

function getAuthHeaders() {
  return { 'Content-Type': 'application/json', ...getCoreAuthHeaders() };
}

// Category configuration — labelKey resolved at render time (never bake a translated
// string into a module-level constant, it would freeze at the language active on load).
const CATEGORIES = [
  { value: 'insurance', labelKey: 'emails.editor.categories.insurance', icon: '🛡️' },
  { value: 'maintenance', labelKey: 'emails.editor.categories.maintenance', icon: '🔧' },
  { value: 'driver', labelKey: 'emails.editor.categories.driver', icon: '👤' },
  { value: 'vehicle', labelKey: 'emails.editor.categories.vehicle', icon: '🚗' },
  { value: 'inspection', labelKey: 'emails.editor.categories.inspection', icon: '📋' },
  { value: 'admin', labelKey: 'emails.editor.categories.admin', icon: '⚙️' }
];

const LANGUAGES = [
  { value: 'fr', labelKey: 'common.language.fr' },
  { value: 'en', labelKey: 'common.language.en' },
  { value: 'de', labelKey: 'emails.editor.languages.de' },
  { value: 'es', labelKey: 'emails.editor.languages.es' }
];

// Variable groups for quick access — same rule: labelKey/descKey only, no baked-in text.
const VARIABLE_GROUPS = {
  driver: {
    labelKey: 'emails.editor.variableGroups.driver.label',
    icon: '👤',
    variables: [
      { name: 'driver.full_name', descKey: 'emails.editor.variableGroups.driver.fields.fullName' },
      { name: 'driver.first_name', descKey: 'emails.editor.variableGroups.driver.fields.firstName' },
      { name: 'driver.last_name', descKey: 'emails.editor.variableGroups.driver.fields.lastName' },
      { name: 'driver.email', descKey: 'emails.editor.variableGroups.driver.fields.email' },
      { name: 'driver.phone', descKey: 'emails.editor.variableGroups.driver.fields.phone' },
      { name: 'driver.license_number', descKey: 'emails.editor.variableGroups.driver.fields.licenseNumber' },
      { name: 'driver.license_expiry', descKey: 'emails.editor.variableGroups.driver.fields.licenseExpiry' },
      { name: 'driver.hire_date', descKey: 'emails.editor.variableGroups.driver.fields.hireDate' },
      { name: 'driver.status', descKey: 'emails.editor.variableGroups.driver.fields.status' }
    ]
  },
  vehicle: {
    labelKey: 'emails.editor.variableGroups.vehicle.label',
    icon: '🚗',
    variables: [
      { name: 'vehicle.display_name', descKey: 'emails.editor.variableGroups.vehicle.fields.displayName' },
      { name: 'vehicle.make', descKey: 'emails.editor.variableGroups.vehicle.fields.make' },
      { name: 'vehicle.model', descKey: 'emails.editor.variableGroups.vehicle.fields.model' },
      { name: 'vehicle.year', descKey: 'emails.editor.variableGroups.vehicle.fields.year' },
      { name: 'vehicle.license_plate', descKey: 'emails.editor.variableGroups.vehicle.fields.licensePlate' },
      { name: 'vehicle.vin', descKey: 'emails.editor.variableGroups.vehicle.fields.vin' },
      { name: 'vehicle.mileage', descKey: 'emails.editor.variableGroups.vehicle.fields.mileage' },
      { name: 'vehicle.fuel_type', descKey: 'emails.editor.variableGroups.vehicle.fields.fuelType' },
      { name: 'vehicle.status', descKey: 'emails.editor.variableGroups.vehicle.fields.status' }
    ]
  },
  insurance: {
    labelKey: 'emails.editor.variableGroups.insurance.label',
    icon: '🛡️',
    variables: [
      { name: 'insurance.contract_number', descKey: 'emails.editor.variableGroups.insurance.fields.contractNumber' },
      { name: 'insurance.insurer_name', descKey: 'emails.editor.variableGroups.insurance.fields.insurerName' },
      { name: 'insurance.start_date', descKey: 'emails.editor.variableGroups.insurance.fields.startDate' },
      { name: 'insurance.expiry_date', descKey: 'emails.editor.variableGroups.insurance.fields.expiryDate' },
      { name: 'insurance.total_premium_ttc', descKey: 'emails.editor.variableGroups.insurance.fields.totalPremiumTtc' },
      { name: 'insurance.coverage_type', descKey: 'emails.editor.variableGroups.insurance.fields.coverageType' },
      { name: 'insurance.policy_number', descKey: 'emails.editor.variableGroups.insurance.fields.policyNumber' },
      { name: 'insurance.status', descKey: 'emails.editor.variableGroups.insurance.fields.status' }
    ]
  },
  claim: {
    labelKey: 'emails.editor.variableGroups.claim.label',
    icon: '⚠️',
    variables: [
      { name: 'claim.claim_number', descKey: 'emails.editor.variableGroups.claim.fields.claimNumber' },
      { name: 'claim.claim_date', descKey: 'emails.editor.variableGroups.claim.fields.claimDate' },
      { name: 'claim.claim_type', descKey: 'emails.editor.variableGroups.claim.fields.claimType' },
      { name: 'claim.description', descKey: 'emails.editor.variableGroups.claim.fields.description' },
      { name: 'claim.estimated_cost', descKey: 'emails.editor.variableGroups.claim.fields.estimatedCost' },
      { name: 'claim.status', descKey: 'emails.editor.variableGroups.claim.fields.status' },
      { name: 'claim.location', descKey: 'emails.editor.variableGroups.claim.fields.location' },
      { name: 'claim.police_report_number', descKey: 'emails.editor.variableGroups.claim.fields.policeReportNumber' }
    ]
  },
  maintenance: {
    labelKey: 'emails.editor.variableGroups.maintenance.label',
    icon: '🔧',
    variables: [
      { name: 'maintenance.title', descKey: 'emails.editor.variableGroups.maintenance.fields.title' },
      { name: 'maintenance.description', descKey: 'emails.editor.variableGroups.maintenance.fields.description' },
      { name: 'maintenance.type', descKey: 'emails.editor.variableGroups.maintenance.fields.type' },
      { name: 'maintenance.priority', descKey: 'emails.editor.variableGroups.maintenance.fields.priority' },
      { name: 'maintenance.status', descKey: 'emails.editor.variableGroups.maintenance.fields.status' },
      { name: 'maintenance.estimated_cost', descKey: 'emails.editor.variableGroups.maintenance.fields.estimatedCost' },
      { name: 'maintenance.actual_cost', descKey: 'emails.editor.variableGroups.maintenance.fields.actualCost' },
      { name: 'maintenance.scheduled_date', descKey: 'emails.editor.variableGroups.maintenance.fields.scheduledDate' },
      { name: 'maintenance.completed_date', descKey: 'emails.editor.variableGroups.maintenance.fields.completedDate' },
      { name: 'maintenance.vendor_name', descKey: 'emails.editor.variableGroups.maintenance.fields.vendorName' },
      { name: 'maintenance.work_order_number', descKey: 'emails.editor.variableGroups.maintenance.fields.workOrderNumber' }
    ]
  },
  inspection: {
    labelKey: 'emails.editor.variableGroups.inspection.label',
    icon: '📋',
    variables: [
      { name: 'inspection.inspection_date', descKey: 'emails.editor.variableGroups.inspection.fields.inspectionDate' },
      { name: 'inspection.type', descKey: 'emails.editor.variableGroups.inspection.fields.type' },
      { name: 'inspection.result', descKey: 'emails.editor.variableGroups.inspection.fields.result' },
      { name: 'inspection.notes', descKey: 'emails.editor.variableGroups.inspection.fields.notes' },
      { name: 'inspection.next_due_date', descKey: 'emails.editor.variableGroups.inspection.fields.nextDueDate' },
      { name: 'inspection.inspector_name', descKey: 'emails.editor.variableGroups.inspection.fields.inspectorName' }
    ]
  },
  organization: {
    labelKey: 'emails.editor.variableGroups.organization.label',
    icon: '🏢',
    variables: [
      { name: 'organization.name', descKey: 'emails.editor.variableGroups.organization.fields.name' },
      { name: 'organization.code', descKey: 'emails.editor.variableGroups.organization.fields.code' },
      { name: 'organization.address', descKey: 'emails.editor.variableGroups.organization.fields.address' },
      { name: 'organization.phone', descKey: 'emails.editor.variableGroups.organization.fields.phone' },
      { name: 'organization.email', descKey: 'emails.editor.variableGroups.organization.fields.email' }
    ]
  },
  date: {
    labelKey: 'emails.editor.variableGroups.date.label',
    icon: '📅',
    variables: [
      { name: 'date.today', descKey: 'emails.editor.variableGroups.date.fields.today' },
      { name: 'date.current_month', descKey: 'emails.editor.variableGroups.date.fields.currentMonth' },
      { name: 'date.current_year', descKey: 'emails.editor.variableGroups.date.fields.currentYear' }
    ]
  }
};

function EmailTemplateEditor({ template, onClose, onSave, onMessage }) {
  const { t } = useTranslation();
  const isEdit = Boolean(template?.id);

  const [formData, setFormData] = useState({
    template_code: '',
    template_name: '',
    category: 'admin',
    language: 'fr',
    subject: '',
    body_text: '',
    body_html: '',
    available_variables: '',
    is_active: true
  });

  const [activeSection, setActiveSection] = useState('basic'); // 'basic' | 'content' | 'variables'
  const [showHtml, setShowHtml] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState([]); // All collapsed by default
  const [copiedVar, setCopiedVar] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (template) {
      setFormData({
        template_code: template.template_code || '',
        template_name: template.template_name || '',
        category: template.category || 'admin',
        language: template.language || 'fr',
        subject: template.subject || '',
        body_text: template.body_text || '',
        body_html: template.body_html || '',
        available_variables: template.available_variables || '',
        is_active: template.is_active !== false
      });
    }
  }, [template]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const insertVariable = (varName, targetField = 'body_text') => {
    const varTag = `{${varName}}`;
    const textarea = document.getElementById(`textarea-${targetField}`);

    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = formData[targetField];
      const newValue = currentValue.substring(0, start) + varTag + currentValue.substring(end);
      handleChange(targetField, newValue);

      // Restore cursor position
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + varTag.length, start + varTag.length);
      }, 0);
    } else {
      // Fallback: append to end
      handleChange(targetField, formData[targetField] + varTag);
    }
  };

  const copyVariable = (varName) => {
    navigator.clipboard.writeText(`{${varName}}`);
    setCopiedVar(varName);
    setTimeout(() => setCopiedVar(null), 2000);
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev =>
      prev.includes(groupKey)
        ? prev.filter(k => k !== groupKey)
        : [...prev, groupKey]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = isEdit
        ? `${API_BASE_URL}/email_templates/${template.id}`
        : `${API_BASE_URL}/email_templates`;

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (result.ok) {
        onSave?.();
      } else {
        throw new Error(result.error?.message || t('common.errorContexts.save'));
      }
    } catch (err) {
      onMessage?.({
        title: t('common.messages.errorTitle'),
        message: err.message,
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-6 py-4 rounded-t-xl">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6" />
              <h2 className="text-xl font-bold">
                {isEdit ? t('emails.editor.title.edit') : t('emails.editor.title.new')}
              </h2>
            </div>
            <button onClick={onClose} className="text-white hover:text-amber-200">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 flex overflow-hidden">
          {/* Left Panel - Form */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Section Tabs */}
            <div className="flex border-b border-gray-200 px-6">
              <button
                type="button"
                onClick={() => setActiveSection('basic')}
                className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                  activeSection === 'basic'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('emails.editor.tabs.basic')}
              </button>
              <button
                type="button"
                onClick={() => setActiveSection('content')}
                className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                  activeSection === 'content'
                    ? 'border-amber-500 text-amber-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {t('emails.editor.tabs.content')}
              </button>
            </div>

            {/* Form Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeSection === 'basic' && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('emails.editor.fields.codeLabel')}
                      </label>
                      <input
                        type="text"
                        value={formData.template_code}
                        onChange={(e) => handleChange('template_code', e.target.value.toUpperCase())}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 font-mono"
                        placeholder="INS_EXPIRY_NOTICE"
                        required
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('emails.editor.fields.codeHint')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('emails.editor.fields.nameLabel')}
                      </label>
                      <input
                        type="text"
                        value={formData.template_name}
                        onChange={(e) => handleChange('template_name', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        placeholder="Avis d'expiration de contrat"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('emails.editor.fields.categoryLabel')}
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => handleChange('category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        required
                      >
                        {CATEGORIES.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.icon} {t(cat.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('emails.editor.fields.languageLabel')}
                      </label>
                      <select
                        value={formData.language}
                        onChange={(e) => handleChange('language', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        required
                      >
                        {LANGUAGES.map(lang => (
                          <option key={lang.value} value={lang.value}>
                            {t(lang.labelKey)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('emails.editor.fields.variablesLabel')}
                    </label>
                    <input
                      type="text"
                      value={formData.available_variables}
                      onChange={(e) => handleChange('available_variables', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 font-mono text-sm"
                      placeholder="driver,vehicle,insurance,date"
                    />
                    <p className="text-xs text-gray-500 mt-1">{t('emails.editor.fields.variablesHint')}</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => handleChange('is_active', e.target.checked)}
                      className="w-4 h-4 text-amber-500 rounded focus:ring-amber-500"
                    />
                    <label htmlFor="is_active" className="text-sm text-gray-700">
                      {t('emails.editor.fields.activeLabel')}
                    </label>
                  </div>
                </div>
              )}

              {activeSection === 'content' && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('emails.editor.fields.subjectLabel')}
                    </label>
                    <input
                      type="text"
                      id="textarea-subject"
                      value={formData.subject}
                      onChange={(e) => handleChange('subject', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                      placeholder="[FleetManager] Votre contrat {insurance.contract_number} expire le {insurance.expiry_date}"
                      required
                    />
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('emails.editor.fields.bodyTextLabel')}
                      </label>
                      <button
                        type="button"
                        onClick={() => setShowHtml(!showHtml)}
                        className="text-xs text-gray-500 hover:text-amber-600 flex items-center gap-1"
                      >
                        <Code className="w-3 h-3" />
                        {showHtml ? t('emails.editor.hideHtml') : t('emails.editor.showHtml')}
                      </button>
                    </div>
                    <textarea
                      id="textarea-body_text"
                      value={formData.body_text}
                      onChange={(e) => handleChange('body_text', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 font-mono text-sm"
                      rows={10}
                      placeholder="Bonjour {driver.full_name},

Votre contrat d'assurance {insurance.contract_number} arrive à expiration le {insurance.expiry_date}.

Véhicule concerné : {vehicle.display_name} ({vehicle.license_plate})

Cordialement,
L'équipe FleetManager"
                      required
                    />
                  </div>

                  {showHtml && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('emails.editor.fields.bodyHtmlLabel')}
                      </label>
                      <textarea
                        id="textarea-body_html"
                        value={formData.body_html}
                        onChange={(e) => handleChange('body_html', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 font-mono text-sm"
                        rows={10}
                        placeholder="<p>Bonjour <strong>{driver.full_name}</strong>,</p>..."
                      />
                      <p className="text-xs text-gray-500 mt-1">{t('emails.editor.fields.bodyHtmlHint')}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100"
                disabled={saving}
              >
                {t('common.actions.cancel')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 flex items-center gap-2 disabled:opacity-50"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {t('common.states.saving')}
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    {isEdit ? t('common.actions.update') : t('common.actions.create')}
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Right Panel - Variables */}
          <div className="w-80 border-l border-gray-200 bg-gray-50 flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center gap-2 text-gray-700">
                <Variable className="w-5 h-5" />
                <h3 className="font-semibold">{t('emails.editor.fields.variablesLabel')}</h3>
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('emails.editor.sidebar.hint')}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {Object.entries(VARIABLE_GROUPS).map(([groupKey, group]) => (
                <div key={groupKey} className="mb-2">
                  <button
                    type="button"
                    onClick={() => toggleGroup(groupKey)}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    {expandedGroups.includes(groupKey) ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                    <span>{group.icon}</span>
                    <span>{t(group.labelKey)}</span>
                    <span className="text-xs text-gray-400 ml-auto">{group.variables.length}</span>
                  </button>
                  {expandedGroups.includes(groupKey) && (
                    <div className="ml-4 space-y-1">
                      {group.variables.map(v => (
                        <div
                          key={v.name}
                          className="flex items-center gap-1 group"
                        >
                          <button
                            type="button"
                            onClick={() => insertVariable(v.name, activeSection === 'content' ? 'body_text' : 'subject')}
                            className="flex-1 flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-amber-50 rounded text-left"
                            title={t('emails.editor.sidebar.insertTitle', { tag: `{${v.name}}` })}
                          >
                            <code className="text-amber-600 font-mono truncate">{`{${v.name}}`}</code>
                          </button>
                          <button
                            type="button"
                            onClick={() => copyVariable(v.name)}
                            className="p-1 text-gray-400 hover:text-amber-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            title={t('emails.editor.sidebar.copyTitle')}
                          >
                            {copiedVar === v.name ? (
                              <CheckCircle className="w-3 h-3 text-green-500" />
                            ) : (
                              <Copy className="w-3 h-3" />
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EmailTemplateEditor;
